import { searchProvidersOutputSchema, sharedTools } from "./sharedTools.mjs";
import { z } from "zod/v4";
import { outdent } from "@repo/utils/utils";

//#region src/harnesses/production/assistantTools.ts
const clientSideError = z.object({
	success: z.literal(false),
	code: z.enum([
		"REJECTED_BY_USER",
		"INPUT_VALIDATION_ERROR",
		"OUTSIDE_BUSINESS_HOURS"
	]),
	message: z.string(),
	suggestion: z.string().optional()
});
const updateProviderInfoOutputSchema = z.discriminatedUnion("success", [
	z.object({
		success: z.literal(true),
		message: z.string(),
		result: z.object({
			updatedProvider: z.object({
				id: z.string(),
				npi: z.string(),
				name: z.string(),
				phoneNumber: z.string(),
				specialty: z.string(),
				faxNumber: z.string().optional(),
				emailAddress: z.string().optional(),
				offersTelehealth: z.boolean().optional(),
				age: z.number().optional(),
				gradYear: z.number().optional(),
				gender: z.enum([
					"male",
					"female",
					"other"
				]).optional(),
				specialtyDetails: z.string().optional()
			}).optional(),
			updatedOrganization: z.object({
				id: z.string(),
				name: z.string(),
				npi: z.string().optional(),
				type: z.string().optional()
			}).optional(),
			updatedLocations: z.array(z.object({
				id: z.string(),
				address: z.string(),
				city: z.string(),
				state: z.string(),
				zip: z.string().optional(),
				phoneNumber: z.string().describe("Phone number formatted with just the numbers, no parentheses or dashes, e.g., 5125550123"),
				faxNumber: z.string().optional().describe("Fax number formatted with just the numbers, no parentheses or dashes, e.g., 5125550123"),
				latitude: z.string().optional(),
				longitude: z.string().optional()
			})).optional()
		})
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("VALIDATION_ERROR"),
		message: z.string()
	}),
	clientSideError
]);
outdent(`
		Update or add new healthcare provider, organization, or location information to the database. This tool will search for existing entities and allow you to either update existing ones or create new entries.

		Use this tool when you need to:
		- Add a new provider to the system with their contact information and specialty
		- Update existing provider contact details (phone, fax, email)
		- Add new healthcare organizations or facilities
		- Add new office locations for existing providers or organizations
		- Link providers to organizations and locations

		The tool will first search for existing providers, organizations, and locations that match your input, then show you options to either select existing entities or create new ones.

		Examples:

		<example>
		Context: User wants to add a new dermatologist they found
		user: "Add Dr. Sarah Johnson, dermatologist, NPI 1234567890, phone 555-555-0123, at Northside Dermatology on 123 Main St, [city], [ST]"
		assistant: "I'll add Dr. Sarah Johnson to the system with her dermatology practice information."
		<commentary>Use when adding new providers with their practice details</commentary>
		</example>

		<example>
		Context: Need to update contact information for existing provider
		user: "Update Dr. Smith's fax number to 555-555-0199"
		assistant: "I'll update Dr. Smith's fax number in our system."
		<commentary>Use when updating existing provider contact information</commentary>
		</example>

		<example>
		Context: Adding a new location for an existing practice
		user: "Add a new Northside Dermatology location at 456 Oak Street, [city], [ST] 12345, phone 555-555-0200"
		assistant: "I'll add the new Northside Dermatology location to our system."
		<commentary>Use when expanding existing practices with new locations</commentary>
		</example>
	`), z.object({
	provider: z.object({
		npi: z.string().length(10, "NPI must be 10 characters"),
		name: z.string().min(1, "Provider name is required"),
		phoneNumber: z.string().min(10, "Provider phone number is required"),
		specialty: z.string().min(1, "Provider specialty is required"),
		faxNumber: z.string().optional(),
		emailAddress: z.string().optional(),
		offersTelehealth: z.boolean().optional(),
		age: z.number().optional(),
		gradYear: z.number().optional(),
		gender: z.enum([
			"male",
			"female",
			"other"
		]).optional(),
		specialtyDetails: z.string().optional()
	}).optional().describe("Provider information to add or update"),
	organization: z.object({
		companyName: z.string().min(1, "Organization name is required"),
		npi: z.string().optional(),
		type: z.string().optional()
	}).optional().describe("Organization information to add or update"),
	locations: z.array(z.object({
		address: z.string().min(1, "Location address is required"),
		city: z.string().min(1, "Location city is required"),
		state: z.string().min(1, "Location state is required"),
		zip: z.string().optional(),
		phoneNumber: z.string().min(1, "Location phone number is required"),
		faxNumber: z.string().optional()
	})).min(1, "At least one location is required").describe("Array of location information to add or update")
}).refine((data) => data.provider || data.organization, { message: "At least one of provider or organization must be provided" });
const assistantTools = {
	call_provider: sharedTools.call_provider,
	search_healthcare_providers: sharedTools.search_healthcare_providers,
	submit_referral_prior_authorization: sharedTools.submit_referral_prior_authorization,
	send_message_to_patient: sharedTools.send_message_to_patient,
	attach_records_send_referral: sharedTools.attach_records_send_referral,
	check_patient_eligibility: sharedTools.check_patient_eligibility
};

//#endregion
export { assistantTools, searchProvidersOutputSchema, updateProviderInfoOutputSchema };